import React, { useState, useEffect, useRef } from 'react'
import {
  Table,
  Input,
  Dropdown,
  Button,
  Icon,
  Label,
  Message,
  Dimmer,
  Checkbox,
  Loader,
  Segment,
  Container,
  Grid,
  Modal,
} from 'semantic-ui-react'
import MickeyForm from '../../../components/Forms/MickeyForm'
import { hasPermission } from '../../../lib/utils/utils'
import { useSelector } from 'react-redux'
import useAxios from '../../../lib/auth/useAxios'
import { toast } from 'react-toastify'
import TemplateRenderer from './TemplateRenderer'
import SmartTagDropdown from './SmartTagDropdown'
import './DocumentTemplate.css'

const DocumentTemplateList = (props) => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [error, setError] = useState(null)
  const [openDocumentForm, setOpenDocumentForm] = useState(false)
  const [openEditModal, setOpenEditModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null) // Holds the template being edited
  const user = useSelector((state) => state.user.user)
  const axios = useAxios()
  const containerRef = useRef(null)
  const tenant = useSelector((state) => {
    return state?.globals?.tenant
  })

  useEffect(() => {
    if (editingTemplate?.css && containerRef.current) {
      // Create a unique ID for this template's styles
      const styleId = `template-style-${editingTemplate.id}`
      let styleElement = document.getElementById(styleId)

      // If style element doesn't exist, create it
      if (!styleElement) {
        styleElement = document.createElement('style')
        styleElement.id = styleId
        styleElement.textContent = editingTemplate.css
        document.head.appendChild(styleElement)
      } else {
        // Update existing style element
        styleElement.textContent = editingTemplate.css
      }

      // Cleanup function
      return () => {
        const element = document.getElementById(styleId)
        if (element) {
          document.head.removeChild(element)
        }
      }
    }
  }, [editingTemplate?.css, editingTemplate?.id])

  const documentTypeOptions = [
    {
      key: 'invoice',
      text: 'Invoice',
      value: 'Invoice',
      variables: {
        deployment: {
          name: 'Your Business Name',
          logo: 'Your Logo',
          deployment_website: 'Your  Website',
          address_1: 'Your Business Address Line 1',
          address_2: 'Your Business Address Line 1',
          city: 'Your Business Address City',
          state: 'Your Business Address State',
          zip_code: 'Your Business Address Zip Code',
        },
        buyer: {
          business: {
            name: 'Business Name',
            contact_first_name: 'Contact First Name',
            contact_last_name: 'Contact Last Name',
            billing_address_line_1: 'Billing Address Line 1',
            billing_address_line_2: 'Billing Address Line 2',
            billing_address_city: 'Billing Address City',
            billing_address_state: 'Billing Address State',
            billing_address_zip_code: 'Billing Address Zip Code',
            contact_phone: 'Billing Address Contact Phone',
          },
        },
        shipping_location: {
          name: 'Shipping Location Name',
          address_1: 'Shipping Location Line 1',
          address_2: 'Shipping Location Line 2',
          city: 'Shipping Location City',
          state: 'Shipping Location State',
          zip_code: 'Shipping Location Zip Code',
          contact_phone: 'Shipping Location Phone Number',
          email: 'Shipping Location Email',
        },
        invoice: {
          invoice_number: 'SO Number',
          invoice_date: 'Date',
          cargo_unit_instances: [{}],
          net_terms: 'Net Terms',
          ship_via: 'Ship Via',
          expected_ship_date: 'Expected Ship Date',
          fob: 'F.O.B.',
          requisitioner: 'Requisitioner',
          payment_terms: 'Payment Terms',
          frt_term: 'FRT Term',
          mill_pickup_number: 'Mill Pickup Number',
          reference_number: 'Reference Number',
          transport_number: 'Transport Number',
          verbal_po: 'Verbal PO',
          sales_tax: 'Sales Tax',
          shipping_cost: 'Shipping Cost',
          subtotal_amount: 'Subtotal Amount',
          total_amount: 'Total Amount',
        },
        invoice_items: [
          {
            quantity: 'Quantity',
            price: 'Price',
            uom: {
              short_name: 'Unit of Measure Short Name',
              singular_name: 'Unit of Measure Singular Name',
              plural_name: 'Unit of Measure Plural Name',
            },
            product_variant_string: 'Product Variant',
          },
        ],
        supplier: {},
      },
    },
    {
      key: 'purchase_order',
      text: 'Purchase Order',
      value: 'Purchase Order',
      variables: {
        deployment: {
          name: 'Your Business Name',
          logo: 'Your Logo',
          deployment_website: 'Your  Website',
          address_1: 'Your Business Address Line 1',
          address_2: 'Your Business Address Line 1',
          city: 'Your Business Address City',
          state: 'Your Business Address State',
          zip_code: 'Your Business Address Zip Code',
        },
        supplier: {
          business: {
            name: 'Business Name',
            contact_first_name: 'Contact First Name',
            contact_last_name: 'Contact Last Name',
            billing_address_line_1: 'Billing Address Line 1',
            billing_address_line_2: 'Billing Address Line 2',
            billing_address_city: 'Billing Address City',
            billing_address_state: 'Billing Address State',
            billing_address_zip_code: 'Billing Address Zip Code',
            contact_phone: 'Billing Address Contact Phone',
          },
        },
        shipping_location: {
          name: 'Shipping Location Name',
          address_1: 'Shipping Location Line 1',
          address_2: 'Shipping Location Line 2',
          city: 'Shipping Location City',
          state: 'Shipping Location State',
          zip_code: 'Shipping Location Zip Code',
          contact_phone: 'Shipping Location Phone Number',
          email: 'Shipping Location Email',
        },
        purchase_order: {
          purchase_order_number: 'PO Number',
          date: 'Date',
          cargo_unit_instances: [{}],
          net_terms: 'Net Terms',
          ship_via: 'Ship Via',
          expected_ship_date: 'Expected Ship Date',
          fob: 'F.O.B.',
          requisitioner: 'Requisitioner',
          payment_terms: 'Payment Terms',
          frt_term: 'FRT Term',
          mill_pickup_number: 'Mill Pickup Number',
          reference_number: 'Reference Number',
          transport_number: 'Transport Number',
          verbal_po: 'Verbal PO',
          sales_tax: 'Sales Tax',
          shipping_cost: 'Shipping Cost',
          subtotal_amount: 'Subtotal Amount',
          total_amount: 'Total Amount',
        },
        purchase_order_items: [
          {
            quantity: 'Quantity',
            price: 'Price',
            uom: {
              short_name: 'Unit of Measure Short Name',
              singular_name: 'Unit of Measure Singular Name',
              plural_name: 'Unit of Measure Plural Name',
            },
            product_variant_string: 'Product Variant',
          },
        ],
        supplier: {},
      },
    },
    {
      key: 'sales_order',
      text: 'Sales Order',
      value: 'Sales Order',
      variables: {
        deployment: {
          name: 'Your Business Name',
          logo: 'Your Logo',
          deployment_website: 'Your  Website',
          address_1: 'Your Business Address Line 1',
          address_2: 'Your Business Address Line 1',
          city: 'Your Business Address City',
          state: 'Your Business Address State',
          zip_code: 'Your Business Address Zip Code',
        },
        buyer: {
          business: {
            name: 'Business Name',
            contact_first_name: 'Contact First Name',
            contact_last_name: 'Contact Last Name',
            billing_address_line_1: 'Billing Address Line 1',
            billing_address_line_2: 'Billing Address Line 2',
            billing_address_city: 'Billing Address City',
            billing_address_state: 'Billing Address State',
            billing_address_zip_code: 'Billing Address Zip Code',
            contact_phone: 'Billing Address Contact Phone',
          },
        },
        shipping_location: {
          name: 'Shipping Location Name',
          address_1: 'Shipping Location Line 1',
          address_2: 'Shipping Location Line 2',
          city: 'Shipping Location City',
          state: 'Shipping Location State',
          zip_code: 'Shipping Location Zip Code',
          contact_phone: 'Shipping Location Phone Number',
          email: 'Shipping Location Email',
        },
        sales_order: {
          sales_order_number: 'SO Number',
          date: 'Date',
          cargo_unit_instances: [{}],
          net_terms: 'Net Terms',
          ship_via: 'Ship Via',
          expected_ship_date: 'Expected Ship Date',
          fob: 'F.O.B.',
          requisitioner: 'Requisitioner',
          payment_terms: 'Payment Terms',
          frt_term: 'FRT Term',
          mill_pickup_number: 'Mill Pickup Number',
          reference_number: 'Reference Number',
          transport_number: 'Transport Number',
          verbal_po: 'Verbal PO',
          sales_tax: 'Sales Tax',
          shipping_cost: 'Shipping Cost',
          subtotal_amount: 'Subtotal Amount',
          total_amount: 'Total Amount',
        },
        sales_order_items: [
          {
            quantity: 'Quantity',
            price: 'Price',
            uom: {
              short_name: 'Unit of Measure Short Name',
              singular_name: 'Unit of Measure Singular Name',
              plural_name: 'Unit of Measure Plural Name',
            },
            product_variant_string: 'Product Variant',
          },
        ],
        supplier: {},
      },
    },
    {
      key: 'change_order',
      text: 'Change Order',
      value: 'Change Order',
      variables: {
        deployment: {
          name: 'Your Business Name',
          logo: 'Your Logo',
          deployment_website: 'Your  Website',
          address_1: 'Your Business Address Line 1',
          address_2: 'Your Business Address Line 1',
          city: 'Your Business Address City',
          state: 'Your Business Address State',
          zip_code: 'Your Business Address Zip Code',
        },
        buyer: {
          business: {
            name: 'Business Name',
            contact_first_name: 'Contact First Name',
            contact_last_name: 'Contact Last Name',
            billing_address_line_1: 'Billing Address Line 1',
            billing_address_line_2: 'Billing Address Line 2',
            billing_address_city: 'Billing Address City',
            billing_address_state: 'Billing Address State',
            billing_address_zip_code: 'Billing Address Zip Code',
            contact_phone: 'Billing Address Contact Phone',
          },
        },
        shipping_location: {
          name: 'Shipping Location Name',
          address_1: 'Shipping Location Line 1',
          address_2: 'Shipping Location Line 2',
          city: 'Shipping Location City',
          state: 'Shipping Location State',
          zip_code: 'Shipping Location Zip Code',
          contact_phone: 'Shipping Location Phone Number',
          email: 'Shipping Location Email',
        },
        change_order: {
          change_order_number: 'CO Number',
          date: 'Date',
          cargo_unit_instances: [{}],
          net_terms: 'Net Terms',
          ship_via: 'Ship Via',
          expected_ship_date: 'Expected Ship Date',
          fob: 'F.O.B.',
          requisitioner: 'Requisitioner',
          payment_terms: 'Payment Terms',
          frt_term: 'FRT Term',
          mill_pickup_number: 'Mill Pickup Number',
          reference_number: 'Reference Number',
          transport_number: 'Transport Number',
          verbal_po: 'Verbal PO',
          sales_tax: 'Sales Tax',
          shipping_cost: 'Shipping Cost',
          subtotal_amount: 'Subtotal Amount',
          total_amount: 'Total Amount',
        },
        sales_order_items: [
          {
            quantity: 'Quantity',
            price: 'Price',
            uom: {
              short_name: 'Unit of Measure Short Name',
              singular_name: 'Unit of Measure Singular Name',
              plural_name: 'Unit of Measure Plural Name',
            },
            product_variant_string: 'Product Variant',
          },
        ],
        supplier: {},
      },
    },
    {
      key: 'trucking_confirmation',
      text: 'Trucking Confirmation',
      value: 'Trucking Confirmation',
      variables: {
        cargo: {
          cargo_unit: {
            singular_name: 'Singular Name',
            plural_name: 'Plural Name',
          },
          pickup_location: 'Pickup Location',
          estimated_ship_date: 'Estimated Pickup Date',
          shipped_date: 'Shipped Date',
          delivery_location: 'Delivery Location',
          estimated_delivery_date: 'Estimated Delivery Date',
          delivery_date: 'Delivery Date',
          stops: [
            {
              location: {
                name: 'Location Name',
                address_1: 'Location Address 1',
                address_2: 'Location Address 2',
                city: 'Location City',
                state: 'Location State',
                zip_code: 'Location Zip Code',
                contact_phone: 'Location Phone Number',
                email: 'Location Email',
              },
            },
          ],
          trucking_company: {
            name: 'Name',
            phone: 'Phone',
          },
          trucking_user: {},
          shipper_load_id: 'Load ID',
          load_id: 'Internal Load ID',
          timeframe: 'Timeframe',
          gross_weight: 'Gross Weight',
          tare_weight: 'Tare Weight',
        },
      },
    },
    { key: 'rfq', text: 'RFQ', value: 'RFQ' },
  ]

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/main/box-items/')
      if (response.status === 200) {
        setTemplates(response.data.results)
        setError(null)
      } else {
        toast.error('Failed to load templates')
      }
    } catch (err) {
      toast.error('Error fetching templates')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (template) => {
    setEditingTemplate(template)
    setOpenEditModal(true)
  }

  const handleEditFormSubmit = (status) => {
    if (status === 'success') {
      setOpenEditModal(false)
      fetchTemplates() // Refresh the list
      toast.success('Template updated successfully')
    } else {
      toast.error('Failed to update template')
    }
  }

  function fillTemplate(template) {
    if (!template) return null
    // Sample fake data
    const fakeData = {
      deployment: {
        deployment_logo: tenant?.deployment_logo,
        name: tenant?.name,
        deployment_address_1: tenant?.deployment_address_1,
        deployment_address_2: tenant?.deployment_address_2,
        deployment_address_city: tenant?.deployment_address_city,
        deployment_address_state: tenant?.deployment_address_state,
        deployment_address_zip_code: tenant?.deployment_address_zip_code,
        deployment_website: tenant?.deployment_website,
      },
      change_order: {
        change_order_number: 'CO-' + Math.floor(Math.random() * 100000),
        date: new Date().toLocaleDateString(),
        subtotal_amount: (Math.random() * 10000).toFixed(2),
        sales_tax: (Math.random() * 1000).toFixed(2),
        shipping_cost: (Math.random() * 100).toFixed(2),
        total_amount: (Math.random() * 12000).toFixed(2),
        ship_via: 'FedEx Ground',
        fob: 'Origin',
        expected_ship_date: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000
        ).toLocaleDateString(),
        requisitioner: 'John Smith',
        payment_terms: 'Net 30',
      },
      sales_order: {
        sales_order_number: 'SO-' + Math.floor(Math.random() * 100000),
        date: new Date().toLocaleDateString(),
        subtotal_amount: (Math.random() * 10000).toFixed(2),
        sales_tax: (Math.random() * 1000).toFixed(2),
        shipping_cost: (Math.random() * 100).toFixed(2),
        total_amount: (Math.random() * 12000).toFixed(2),
        ship_via: 'FedEx Ground',
        fob: 'Origin',
        expected_ship_date: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000
        ).toLocaleDateString(),
        requisitioner: 'John Smith',
        payment_terms: 'Net 30',
      },
      purchase_order: {
        purchase_order_number: 'PO-' + Math.floor(Math.random() * 100000),
        date: new Date().toLocaleDateString(),
        subtotal_amount: (Math.random() * 10000).toFixed(2),
        sales_tax: (Math.random() * 1000).toFixed(2),
        shipping_cost: (Math.random() * 100).toFixed(2),
        total_amount: (Math.random() * 12000).toFixed(2),
        ship_via: 'FedEx Ground',
        fob: 'Origin',
        expected_ship_date: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000
        ).toLocaleDateString(),
        requisitioner: 'John Smith',
        payment_terms: 'Net 30',
      },
      invoice: {
        invoice_number: 'IN-' + Math.floor(Math.random() * 100000),
        invoice_date: new Date().toLocaleDateString(),
        subtotal_amount: (Math.random() * 10000).toFixed(2),
        sales_tax: (Math.random() * 1000).toFixed(2),
        shipping_cost: (Math.random() * 100).toFixed(2),
        total_amount: (Math.random() * 12000).toFixed(2),
        ship_via: 'FedEx Ground',
        fob: 'Origin',
        expected_ship_date: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000
        ).toLocaleDateString(),
        requisitioner: 'John Smith',
        payment_terms: 'Net 30',
      },
      buyer: {
        business: {
          name: 'Domestic Buying Inc.',
          billing_address_line_1: '123 Buying Street',
          billing_address_line_2: '',
          billing_address_city: 'New York',
          billing_address_state: 'NY',
          billing_address_zip_code: '01100',
          contact_phone: '(415) 123-0123',
        },
      },
      supplier: {
        business: {
          name: 'Global Supplies Inc.',
          billing_address_line_1: '456 Vendor Street',
          billing_address_line_2: 'Unit 100',
          billing_address_city: 'Chicago',
          billing_address_state: 'IL',
          billing_address_zip_code: '60601',
          contact_phone: '(312) 555-0123',
        },
      },
      shipping_location: {
        name: 'Carriage House',
        address_1: '16 Clarke Lane',
        address_2: '',
        city: 'Providence',
        state: 'RI',
        zip_code: '02906',
        contact_phone: '(510) 219-4402',
      },
      order: {
        total_supplier_quantity: '100',
        order_number: 'ORD-' + Math.floor(Math.random() * 100000),
        created_at: new Date().toLocaleDateString(),
        project_name: 'Project ABC',
        total: (Math.random() * 2000).toFixed(2),
        buyer_total: (Math.random() * 200).toFixed(2),
        buyer: {
          business: {
            name: 'Domestic Buying Inc.',
            billing_address_line_1: '123 Buying Street',
            billing_address_line_2: '',
            billing_address_city: 'New York',
            billing_address_state: 'NY',
            billing_address_zip_code: '01100',
            contact_phone: '(415) 123-0123',
          },
        },
        supplier: {
          business: {
            name: 'Global Supplies Inc.',
            billing_address_line_1: '456 Vendor Street',
            billing_address_line_2: 'Unit 100',
            billing_address_city: 'Chicago',
            billing_address_state: 'IL',
            billing_address_zip_code: '60601',
            contact_phone: '(312) 555-0123',
          },
        },
        pickup_address: {
          name: 'Acme Warehouse',
          address_1: '16 Clarke Lane',
          address_2: '',
          city: 'Providence',
          state: 'RI',
          zip_code: '02906',
          contact_phone: '(510) 219-4402',
        },
        delivery_address: {
          name: 'Carriage House',
          address_1: '16 Clarke Lane',
          address_2: '',
          city: 'Providence',
          state: 'RI',
          zip_code: '02906',
          contact_phone: '(510) 219-4402',
        },
        owner: {
          name: 'Owner Inc.',
          billing_address_line_1: '123 Owner Street',
          billing_address_line_2: '',
          billing_address_city: 'New York',
          billing_address_state: 'NY',
          billing_address_zip_code: '01100',
          contact_phone: '(415) 123-0123',
        },
        general_contractor: {
          name: 'General Contractor Inc.',
          billing_address_line_1: '123 GC Street',
          billing_address_line_2: '',
          billing_address_city: 'New York',
          billing_address_state: 'NY',
          billing_address_zip_code: '01100',
          contact_phone: '(415) 123-0123',
        },
        lender: {
          name: 'Lender Inc.',
          billing_address_line_1: '123 Lender Street',
          billing_address_line_2: '',
          billing_address_city: 'New York',
          billing_address_state: 'NY',
          billing_address_zip_code: '01100',
          contact_phone: '(415) 123-0123',
        },
        subcontractor: {
          name: 'Subcontractor Inc.',
          billing_address_line_1: '123 Sub Street',
          billing_address_line_2: '',
          billing_address_city: 'New York',
          billing_address_state: 'NY',
          billing_address_zip_code: '01100',
          contact_phone: '(415) 123-0123',
        },
        lien_agent: {
          name: 'Lien Agent Inc.',
          billing_address_line_1: '123 Lien Street',
          billing_address_line_2: '',
          billing_address_city: 'New York',
          billing_address_state: 'NY',
          billing_address_zip_code: '01100',
          contact_phone: '(415) 123-0123',
        },
        bonding_company: {
          name: 'Bonding Company Inc.',
          billing_address_line_1: '123 Bonding Street',
          billing_address_line_2: '',
          billing_address_city: 'New York',
          billing_address_state: 'NY',
          billing_address_zip_code: '01100',
          contact_phone: '(415) 123-0123',
        },
        po_number: '123',
        so_number: '321',
        estimated_ship_date: new Date().toLocaleDateString(),
        estimated_delivery_date: new Date().toLocaleDateString(),
        buyer_subtotal: (Math.random() * 100).toFixed(2),
        sales_tax_total: (Math.random() * 100).toFixed(2),
        shipping_total: (Math.random() * 100).toFixed(2),
        total_amount: (Math.random() * 100).toFixed(2),
      },
      order_items: [
        {
          quantity: Math.floor(Math.random() * 100),
          buyer_quantity: Math.floor(Math.random() * 100),
          unit: { short_name: 'EA' },
          buyer_unit: { short_name: 'EA' },
          order_item_name: 'Widget Type A',
          price: (Math.random() * 100).toFixed(2),
          secondary_delivered_price: (Math.random() * 100).toFixed(2),
          total: (Math.random() * 1000).toFixed(2),
          buyer_total: (Math.random() * 1000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 50),
          buyer_quantity: Math.floor(Math.random() * 50),
          unit: { short_name: 'BOX' },
          buyer_unit: { short_name: 'BOX' },
          order_item_name: 'Premium Supplies B',
          price: (Math.random() * 200).toFixed(2),
          secondary_delivered_price: (Math.random() * 200).toFixed(2),
          total: (Math.random() * 2000).toFixed(2),
          buyer_total: (Math.random() * 200).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 25),
          buyer_quantity: Math.floor(Math.random() * 25),
          unit: { short_name: 'CS' },
          buyer_unit: { short_name: 'CS' },
          order_item_name: 'Deluxe Package C',
          price: (Math.random() * 300).toFixed(2),
          secondary_delivered_price: (Math.random() * 300).toFixed(2),
          delivered_price_no_sales_tax: (Math.random() * 3000).toFixed(2),
          total: (Math.random() * 2000).toFixed(2),
          buyer_total: (Math.random() * 200).toFixed(2),
        },
      ],
      change_order_items: [
        {
          quantity: Math.floor(Math.random() * 100),
          uom: { short_name: 'EA' },
          product_variant_string: 'Widget Type A',
          price: (Math.random() * 100).toFixed(2),
          total: (Math.random() * 1000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 50),
          uom: { short_name: 'BOX' },
          product_variant_string: 'Premium Supplies B',
          price: (Math.random() * 200).toFixed(2),
          total: (Math.random() * 2000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 25),
          uom: { short_name: 'CS' },
          product_variant_string: 'Deluxe Package C',
          price: (Math.random() * 300).toFixed(2),
          total: (Math.random() * 3000).toFixed(2),
        },
      ],
      sales_order_items: [
        {
          quantity: Math.floor(Math.random() * 100),
          uom: { short_name: 'EA' },
          product_variant_string: 'Widget Type A',
          price: (Math.random() * 100).toFixed(2),
          total: (Math.random() * 1000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 50),
          uom: { short_name: 'BOX' },
          product_variant_string: 'Premium Supplies B',
          price: (Math.random() * 200).toFixed(2),
          total: (Math.random() * 2000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 25),
          uom: { short_name: 'CS' },
          product_variant_string: 'Deluxe Package C',
          price: (Math.random() * 300).toFixed(2),
          total: (Math.random() * 3000).toFixed(2),
        },
      ],
      purchase_order_items: [
        {
          quantity: Math.floor(Math.random() * 100),
          uom: { short_name: 'EA' },
          product_variant_string: 'Widget Type A',
          price: (Math.random() * 100).toFixed(2),
          total: (Math.random() * 1000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 50),
          uom: { short_name: 'BOX' },
          product_variant_string: 'Premium Supplies B',
          price: (Math.random() * 200).toFixed(2),
          total: (Math.random() * 2000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 25),
          uom: { short_name: 'CS' },
          product_variant_string: 'Deluxe Package C',
          price: (Math.random() * 300).toFixed(2),
          total: (Math.random() * 3000).toFixed(2),
        },
      ],
      invoice_items: [
        {
          quantity: Math.floor(Math.random() * 100),
          uom: { short_name: 'EA' },
          product_variant_string: 'Widget Type A',
          price: (Math.random() * 100).toFixed(2),
          total: (Math.random() * 1000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 50),
          uom: { short_name: 'BOX' },
          product_variant_string: 'Premium Supplies B',
          price: (Math.random() * 200).toFixed(2),
          total: (Math.random() * 2000).toFixed(2),
        },
        {
          quantity: Math.floor(Math.random() * 25),
          uom: { short_name: 'CS' },
          product_variant_string: 'Deluxe Package C',
          price: (Math.random() * 300).toFixed(2),
          total: (Math.random() * 3000).toFixed(2),
        },
      ],
      signature: '',
      date: new Date().toLocaleDateString(),
      website: 'www.acmecorp.com/terms',
    }

    // Helper function to safely get nested object values
    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : ''
      }, obj)
    }

    // Handle for loops first
    let processedTemplate = template.replace(
      /{% for (\w+) in (\w+) %}([\s\S]*?){% endfor %}/g,
      (match, itemName, collection, content) => {
        const items = fakeData[collection]
        if (!Array.isArray(items)) return ''

        return items
          .map((itemData) => {
            return content.replace(/{{ ([\w\.]+) }}/g, (match, path) => {
              // If the path starts with the item name, replace it with the current item data
              if (path.startsWith(itemName + '.')) {
                const itemPath = path.substring(itemName.length + 1)
                return getNestedValue(itemData, itemPath) || ''
              }
              // Otherwise, look up in the main data object
              return getNestedValue(fakeData, path) || ''
            })
          })
          .join('\n')
      }
    )

    // Handle regular variable replacements
    processedTemplate = processedTemplate.replace(
      /{{ ([\w\.]+) }}/g,
      (match, path) => {
        return getNestedValue(fakeData, path) || ''
      }
    )

    return processedTemplate
  }

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template?.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = !selectedType || template.box_item === selectedType
    return matchesSearch && matchesType
  })

  const handleSignatureToggle = async (template) => {
    try {
      const updatedTemplate = {
        requires_signature: !template.requires_signature,
      }
      const response = await axios.patch(
        `/main/box-items/${template.id}/`,
        updatedTemplate
      )

      if (response.status === 200) {
        toast.success('Default template updated successfully')
        fetchTemplates() // Refresh the templates to update UI
      } else {
        toast.error('Failed to update default template')
      }
    } catch (error) {
      console.error('Error updating default template:', error)
      toast.error('Error updating default template')
    }
  }
  const handleDefaultToggle = async (template) => {
    try {
      const updatedTemplate = { is_default: !template.is_default }
      const response = await axios.patch(
        `/main/box-items/${template.id}/`,
        updatedTemplate
      )

      if (response.status === 200) {
        toast.success('Default template updated successfully')
        fetchTemplates() // Refresh the templates to update UI
      } else {
        toast.error('Failed to update default template')
      }
    } catch (error) {
      console.error('Error updating default template:', error)
      toast.error('Error updating default template')
    }
  }

  if (loading) {
    return (
      <Segment>
        <Dimmer active inverted>
          <Loader>Loading Templates</Loader>
        </Dimmer>
      </Segment>
    )
  }

  return (
    <>
      <Container fluid>
        <Button
          color="green"
          floated="right"
          icon
          labelPosition="left"
          onClick={() => setOpenDocumentForm(true)}
        >
          <Icon name="plus" />
          New Template
        </Button>
        <div style={{ clear: 'both' }}></div>
        <Segment>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <Input
              icon="search"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1 }}
            />
            <Dropdown
              selection
              options={documentTypeOptions}
              value={selectedType}
              onChange={(_, { value }) => setSelectedType(value)}
              placeholder="Filter by type"
              style={{ minWidth: '200px' }}
            />
          </div>
        </Segment>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Description</Table.HeaderCell>
              <Table.HeaderCell>Format</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Live</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">
                Requires Signature
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Edit</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredTemplates.map((template) => (
              <Table.Row
                key={template?.id}
                positive={template?.is_default}
                negative={!template?.is_default}
              >
                <Table.Cell>{template?.display_name}</Table.Cell>
                <Table.Cell>{template?.box_item}</Table.Cell>
                <Table.Cell>{template?.description || '-'}</Table.Cell>
                <Table.Cell>
                  <Label
                    color={template.content_type === 'html' ? 'blue' : 'grey'}
                    size="small"
                  >
                    {template?.content_type?.toUpperCase()}
                  </Label>
                </Table.Cell>
                <Table.Cell textAlign="center" width={1}>
                  <Checkbox
                    toggle
                    checked={template.is_default}
                    onChange={() => handleDefaultToggle(template)}
                  />
                </Table.Cell>
                <Table.Cell textAlign="center" width={1}>
                  <Checkbox
                    toggle
                    checked={template.requires_signature}
                    onChange={() => handleSignatureToggle(template)}
                  />
                </Table.Cell>
                <Table.Cell textAlign="center" width={1}>
                  <Button icon onClick={() => handleEdit(template)}>
                    <Icon name="edit" />
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
            {filteredTemplates.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="7" textAlign="center">
                  <Message info>No templates found</Message>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Container>

      {/* Add New Template Modal */}
      <Modal
        onClose={() => setOpenDocumentForm(false)}
        open={openDocumentForm && hasPermission(user, 'Create Document')}
      >
        <Modal.Header>Add New Document Template</Modal.Header>
        <Modal.Content>
          <MickeyForm
            formName="BoxItemForm"
            model="BoxItem"
            url="/main/box-items/"
            tenantAware={false}
            currentPortal={props.currentPortal}
            values={{
              is_default: false,
            }}
            successMessage="Template created successfully"
            failureMessage="Failed to create template"
            handleForm={(status) => {
              if (status === 'success') {
                setOpenDocumentForm(false)
                fetchTemplates()
              }
            }}
          />
        </Modal.Content>
      </Modal>

      {/* Edit Template Modal */}
      <Modal
        size="fullscreen"
        onClose={() => setOpenEditModal(false)}
        open={openEditModal}
      >
        <Modal.Header>Edit Document Template</Modal.Header>
        <Modal.Content>
          <div className="template-editor-container">
            <div className="template-editor-grid">
              <div className="form-column">
                <MickeyForm
                  formName="BoxItemForm"
                  model="BoxItem"
                  url={`/main/box-items/`}
                  instanceId={editingTemplate?.id}
                  tenantAware={false}
                  currentPortal={props.currentPortal}
                  successMessage="Template updated successfully"
                  failureMessage="Failed to update template"
                  handleForm={handleEditFormSubmit}
                  onUpdate={(d) => {
                    setEditingTemplate(d)
                  }}
                />
              </div>
              <div className="preview-column">
                <div className="preview-content">
                  <SmartTagDropdown
                    documentType={editingTemplate?.box_item_type}
                    documentTypeOptions={documentTypeOptions}
                    onSelect={(d) => {}}
                  />
                  <TemplateRenderer
                    html={fillTemplate(editingTemplate?.content)}
                    css={editingTemplate?.css}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal.Content>
      </Modal>
    </>
  )
}

export default DocumentTemplateList
